import React, { useEffect, useState } from "react";
import { useCurrentHousehold } from "../contexts/AuthProvider";
import { getIngredientList, IngredientListItem, Ingredient, saveIngredientList } from "../data";
import { DragDropContext, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import DraggableIngredient from "./base/DraggableIngredient";
import { UniqueId } from "../utils/unique";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import AddIngredient, { IngredientOption } from "./AddIngredient";
import { remove, reorder } from "../utils/list";
import DeleteIcon from "@mui/icons-material/Delete";

interface BaseIngredient extends IngredientListItem {
    uniqueId: string;
}

const gen = new UniqueId();

export interface BaseIngredientsProps {
    ingredients: IngredientOption[];
}

export default function BaseIngredients({ ingredients }: BaseIngredientsProps) {
    const currentHousehold = useCurrentHousehold();
    const theme = useTheme();
    const [baseIngredients, setBaseIngredients] = useState<BaseIngredient[] | undefined>();

    useEffect(() => {
        if (!currentHousehold) return;

        getIngredientList(currentHousehold.base_ingredients_list_id).then(list => {
            setBaseIngredients(list?.items.map(item => ({ ...item, uniqueId: gen.next() })));
        });
    }, [currentHousehold, setBaseIngredients]);

    const onAddIngredient = (ingredient: Ingredient) => {
        if (!currentHousehold) return;

        setBaseIngredients(old => {
            if (!old) return old;

            const newList: BaseIngredient[] = [...old, { ...ingredient, uniqueId: gen.next() }];
            saveIngredientList(currentHousehold.base_ingredients_list_id, { items: newList });
            return newList;
        });
    };

    const onDragEnd: OnDragEndResponder = result => {
        const { source, destination } = result;
        if (!destination || !baseIngredients || !currentHousehold) {
            return;
        }

        const newList =
            destination.droppableId === "trash-can"
                ? remove(baseIngredients, source.index)
                : reorder(baseIngredients, source.index, destination.index);
        saveIngredientList(currentHousehold.base_ingredients_list_id, { items: newList });
        setBaseIngredients(newList);
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Stack direction="column" flexGrow={1}>
                <AddIngredient ingredients={ingredients} onAdd={onAddIngredient}></AddIngredient>
                <Box paddingTop="15px">
                    <Droppable droppableId="base-ingredients">
                        {(provided, snapshot) => (
                            <Stack {...provided.droppableProps} ref={provided.innerRef} direction="column">
                                {baseIngredients?.map((item, index) => (
                                    <DraggableIngredient
                                        key={item.name}
                                        id={item.name}
                                        title={item.name}
                                        index={index}
                                    />
                                ))}
                                {provided.placeholder}
                            </Stack>
                        )}
                    </Droppable>
                </Box>
                <Box paddingTop="30px">
                    <Droppable droppableId="trash-can">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{
                                    backgroundColor: snapshot.isDraggingOver ? theme.palette.primary.main : "lightgray",
                                    minHeight: "90px",
                                    position: "relative",
                                }}
                            >
                                <Stack
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    position="absolute"
                                    left={0}
                                    right={0}
                                    top={0}
                                    bottom={0}
                                    color={"gray"}
                                >
                                    <DeleteIcon></DeleteIcon>
                                    <Typography variant="button">Drag ingredients here to remove</Typography>
                                </Stack>
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </Box>
            </Stack>
        </DragDropContext>
    );
}
